import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import CtaButton from '../../ctaButton/ctaButton';
import heroPrivacySecurityPd from '../images/heroPrivacySecurityPd.svg';
import Tag from '../../tag/tag';
import styles from './heroHomePrequalCard.module.scss';
import trackHeapEvent from '../../../services/helpers/trackHeapEvent';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import { capitalizeFirstLetter } from '../../../utils/util';
import SmartImage from '../../smartImage/smartImage';

function HeroHomePrequalCard({
  url = '',
  loanPurposes = null,
  contentNote = '',
  contentEmail = null,
  headline = '',
  submitBtnText = 'Check Your Rate',
  submitBtnVariant = '',
  submitBtnColor = '',
  theme = '',
  experimentName,
  experimentVariation,
  privacyImage,
  isPersonalization = false,
}) {
  const { width } = useWindowDimensions();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (width < 768) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [width]);

  const heroEmailTrack = (analyticsKey) => {
    trackHeapEvent(
      {
        Name: 'Home Page Hero - Respond to Mail Offer',
        CTA: analyticsKey,
      },
      'CMS_Unification'
    );
  };

  const handleSubmit = (purpose) => {
    const loanPurpose = purpose;
    const loanUrl = loanPurposes.find((item) => item.value === loanPurpose);
    const redUrl = loanUrl?.url ?? url;
    let experiment = '';
    if (experimentName && experimentVariation) {
      experiment = `experiment=${experimentName}__${capitalizeFirstLetter(
        experimentVariation
      )}`;
    }
    window.location.assign(
      `${redUrl}?loanPurpose=${loanPurpose}${
        experiment ? `&${experiment}` : ''
      }`
    );
  };

  const renderEmailLink = () =>
    contentEmail && (
      <div className={styles.heroPrequalCardActionsMail}>
        <div className={styles.heroPrequalActionsMailLinkWrapper}>
          <a
            data-testid="email-offer"
            href={contentEmail.link}
            onClick={() => heroEmailTrack(contentEmail.heapEventKey)}
            className={`${styles.heroPrequalCardActionsMailLink} ${
              isPersonalization
                ? styles.heroPersonilaztionPrequalCardTextColor
                : ''
            }`}
          >
            <span
              className={`${styles.contentEmailIcon} ${
                isPersonalization ? styles.personalizationMailLink : ''
              }`}
              role="img"
            />
            <div
              className={`${styles.mailLinkText} ${
                isPersonalization ? styles.personalizationMailLinkText : ''
              }`}
            >
              {contentEmail.text}
            </div>
          </a>
        </div>
      </div>
    );

  const renderPrivacyImage = () => (
    <div>
      <SmartImage
        src={heroPrivacySecurityPd.src}
        alt="Privacy & Security"
        loading="lazy"
        width={heroPrivacySecurityPd.width}
        height={heroPrivacySecurityPd.height}
      />
    </div>
  );

  return (
    <div
      className={`${styles.heroPrequal} ${styles.heroPrequalDocked} ${
        theme ? styles.theme : ''
      } ${!isPersonalization && isMobile ? styles.marginTop : ''}`}
    >
      <div className={styles.heroPrequalContainer}>
        {headline && (
          <div
            className={`${styles.heroPrequalHeadline} ${
              isPersonalization ? styles.heroPersonilaztionPrequalHeadline : ''
            }`}
          >
            {parse(headline)}
          </div>
        )}
        {contentNote && (
          <div
            className={`${styles.heroPrequalCardText} ${
              isPersonalization
                ? styles.heroPersonilaztionPrequalCardTextColor
                : ''
            }`}
          >
            {parse(contentNote)}
          </div>
        )}
        <div className={styles.heroPrequalCardContainer}>
          {loanPurposes?.map((loanPurpose, index) => (
            <div key={loanPurpose.headline} className={styles.heroPrequalCard}>
              <div />
              <div className={styles.heroPrequalCardPromotionWrapper}>
                {loanPurpose.promotion && (
                  <div>
                    <div className={styles.heroPrequalCardPromotion}>
                      <div>
                        <Tag label={loanPurpose.promotion} />
                      </div>
                    </div>
                  </div>
                )}
                <div className={styles.heroPrequalCardContentWrapper}>
                  <div className={styles.heroPrequalCardMobileWrapper}>
                    {isMobile && (
                      <div className={styles.heroPrequalCardTitle}>
                        <SmartImage
                          data-testid="mobile-icon"
                          src={loanPurpose?.headlineImage?.fields?.file.url}
                          alt={
                            loanPurpose.headlineImage?.alt ??
                            loanPurpose.headline ??
                            ''
                          }
                          priority
                          width={48}
                          height={48}
                        />
                      </div>
                    )}
                    <div className={styles.heroHomePrequalCardMobileSubWrapper}>
                      <div className={`${styles.heroPrequalCardTitle}`}>
                        {!isMobile && (
                          <SmartImage
                            src={loanPurpose?.headlineImage?.fields?.file.url}
                            alt={
                              loanPurpose.headlineImage?.alt ??
                              loanPurpose.headline ??
                              ''
                            }
                            data-testid="desktop-icon"
                            priority
                            width={48}
                            height={48}
                          />
                        )}
                        <div>{parse(loanPurpose.headline ?? '')}</div>
                      </div>
                      <div className={styles.heroPrequalCardDescription}>
                        {loanPurpose.description}
                      </div>
                      <CtaButton
                        type="submit"
                        data-testid={`check-rate-${index}`}
                        className={`${styles.homePrequalCardsubmitBtn} ${
                          isPersonalization
                            ? styles.personalizationCardButton
                            : ''
                        }`}
                        label={submitBtnText}
                        variant={submitBtnVariant}
                        color={submitBtnColor}
                        aria-label="submit-rate"
                        onClick={() => handleSubmit(loanPurpose.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div
          className={`${styles.heroPrequalCardFooter} ${
            isPersonalization ? styles.heroPersonilaztionPrequalCardFooter : ''
          }`}
        >
          {!isMobile ? (
            <>
              {renderEmailLink()}
              {privacyImage && renderPrivacyImage()}
            </>
          ) : (
            <div className={styles.mobileFooter}>
              {privacyImage && renderPrivacyImage()}
              {renderEmailLink()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

HeroHomePrequalCard.propTypes = {
  url: PropTypes.string,
  loanPurposes: PropTypes.arrayOf(PropTypes.object),
  contentNote: PropTypes.string,
  contentEmail: PropTypes.object,
  headline: PropTypes.string,
  submitBtnText: PropTypes.string,
  submitBtnVariant: PropTypes.string,
  submitBtnColor: PropTypes.string,
  theme: PropTypes.string,
};

export default HeroHomePrequalCard;
